import React, { useState, useEffect } from "react"
import { connect } from 'react-redux';
import {
  Link, useLocation, useHistory
} from "react-router-dom";
import { Helmet } from "react-helmet";
import ReactTooltip from 'react-tooltip';
import { Popover, Transition } from '@headlessui/react'

//import { InformationCircleIcon } from '@heroicons/react/solid'

import ErrorBoundary from "../../components/common/errorboundary";
import LoaderFancy from "../../components/common/loaderfancy";
import ErrorBar from "../../components/common/errorBar";
import SubredditTags from "../reddit/subreddittags";

import { collectionsService } from '../../services/collections';
import { audiencesOperations } from "../../redux/audiences"
import { discoverService } from '../../services/discover';
import { redditService } from '../../services/reddit';

import { getParamsFromUrl, toQueryString } from '../../utils/urls';
import { trackEvent } from '../../utils/tracking';
import { readableNumber } from '../../utils/text';


// Updates discovery page which uses new API
const AudienceDiscoveryNew = ({
  currentUser,
  currentCollection,  // set if adding to existing collection, otherwise blank
  currentCollectionItems,  // set if adding to existing collection, otherwise blank
  refetchCurrentCollection,
  fetchAudiences,
}) => {
  // state
  const location = useLocation();
  const history = useHistory();
  const params = getParamsFromUrl();

  const [name, setName] = useState(params.name || currentCollection?.name || '');
  const [keywords, setKeywords] = useState(currentCollection ? [currentCollection?.name] : (params.keywords ? params.keywords.split(',') : []), [location]);
  const [selectedSubs, setSelectedSubs] = useState([]);  // array of subreddit NAMES user has selected
  const [results, setResults] = useState([]); // this will be an array of communities
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  

  // effects
  useEffect(() => {
    if (!name){
      // Always needs a name, otherwise go name this new audience
      history.push('/audiences/new/')
    }
  }, [name]);


  useEffect(() => {
    if (keywords && keywords.length) performSearch(keywords);
  }, [
    keywords,
  ]); // Run on first time

  // actions
  const performSearch = (searchKeywords) => {
    if (!searchKeywords || !searchKeywords.length){
      setResults([]);
      return;
    }
    setLoading(true);
    setResults([]);
    setError(null);
    trackEvent("PerformCommunitySearch", {
      "type": "Discover",
      "count_keywords": searchKeywords.length,
      "keywords": searchKeywords
    });
    let params = {
      'keyword': searchKeywords.map(k => encodeURIComponent(k)),
    }
    if (currentCollection?.hash){
      params['collection'] = currentCollection.hash;
    }
    discoverService.performAudienceSearch(
      params, (response) => {
      setResults(response.data.communities);
      setSelectedSubs(response.data.communities.filter(c => c.preselected).map(c => c.name));
      fetchMissingSubredditDetails(response.data.communities.filter(c => !c.data).map(c => c.name));
      setLoading(false);

    }, (error, response) => {
      setLoading(false);
      if (error.response && error.response.status === 500){
        setError("Error fetching results.")
      } else {
        setError(response || "Error fetching results.")
      }
    });
  }

  const fetchMissingSubredditDetails = (subredditNames) => {
    // For subs that aren't in DB, go fetch them via API asynchronously
    if (!subredditNames || !subredditNames.length){
      return;
    }

    redditService.fetchSubreddits(subredditNames, (response) => {
      // append the new data to existing results
      setResults(prevResults => (
        prevResults.map(c => {
          if (subredditNames.includes(c.name)){
            return {
              ...c,
              data: response.data.find(r => r.name === c.name)
            }
          }
          return c;
        })
      ));

    }, (error) => {
      console.error(error);
    });
  }

  const setNewKeywords = (keywords) => {
    setKeywords(keywords);
    // change url (different if we have a collection)
    if (currentCollection){
      history.push(`/audience/${currentCollection.hash}/add/discover/?name=${name}&keywords=${keywords.join(",")}`);
    } else {
      history.push(`/audiences/new/discover/?name=${name}&keywords=${keywords.join(",")}`);
    }

    if (document.getElementById('results-container')){
      document.getElementById('results-container').scrollTop = 0;
    }
  }

  const clearKeyword = (keyword) => {
    setNewKeywords(keywords.filter(t => t !== keyword))
  }

  const addKeyword = (keyword) => {
    if (!keyword) return;
    if (keywords.includes(keyword)) return;

    if (keyword.includes(',')){
      // adding multiple (comma separated)
      setNewKeywords([...keywords, ...keyword.split(',').map(k => k.trim())])
    } else {
      // just adding one
      setNewKeywords([...keywords, keyword])
    }
  }

  const addToAudience = () => {
    if (!selectedSubs || !selectedSubs.length){
      alert("Missing selected subreddits")
      return;
    }
    if (!name && !currentCollection){
      alert("Missing Audience Name")
      return;
    }

    // if there's an audience, add to it. otherwise create audience and add to it
    if (!currentCollection){
      createCollectionAndAdd();
    } else {
      bulkAdd(currentCollection)
    }
  }

  const createCollectionAndAdd = () => {
    setCreating(true)
    collectionsService.createCollection(name, (results) => {
      setCreating(false)
      if (results && results.data){
        bulkAdd(results.data);
        fetchAudiences()
      } else {
        console.log(error)
        setError("Unknown error")
      }
    }, (response, error) => {
      console.log(error)
      setCreating(false)
      setError(error)
    })
  }

  const bulkAdd = (collection) => {
    setCreating(true)
    // assuming all are subreddits for now
    const items = selectedSubs.map((s) => {return {'external_type': 'RSR', 'external_id': s}});
    collectionsService.addCollectionItems(collection.hash, items, (results) => {
      if (results && results.data){
        // go to the audience
        setCreating(false);

        // if we just created, simply go there. otherwise refetch then go there
        if (currentCollection){
          refetchCurrentCollection()
        }
        history.push(`/audience/${collection.hash}/`);
        fetchAudiences();
      }
    }, (response, error) => {
      console.log(error)
      setCreating(false)
      setError(error)
    })
  }

  return (
    <div className="w-full">
      <Helmet><title>Audiences | New | Discover</title></Helmet>

      <ErrorBoundary>
        {loading && !results.length ? (
          <React.Fragment>
            <div className="h-80">
              <LoaderFancy special={true} message="Finding the BEST subreddits for you..."/>
            </div>
            {/* its a spacer */}
            {currentCollection ? (
              <div className="sm:h-36" />
            ) : (
              <div className="sm:h-40" />
            )}
          </React.Fragment>
        ) : (
          <React.Fragment>
            {results.length ? (
              <div className="mx-auto p-2 px-3 text-semibold bg-green-600 rounded-md mb-4">
                Found {results.length} subreddits. Click to select which ones to include in your audience
                {results.filter(c => c.preselected).length > 0 ? ` (${results.filter(c => c.preselected).length} pre-selected because they seem relevant)` : ''}
              </div>
            ) : !loading && (results.length === 0) ? (
              <div className="mx-auto p-2 px-3 text-semibold bg-yellow-600 rounded-md mb-4">
                No subreddit results found. Please <Link to="/audiences/new/" className="underline">click here</Link> to try again with a different search query.
              </div>
            ) : ''}
            
            {/* <div className="flex">
              <h3 className="mb-2 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                Search keywords
              </h3>
              <div className="opacity-50 cursor-pointer hover:opacity-75 ml-2" data-for='search-keywords-tooltip'
                data-tip="Try a 2-4 keywords your audience might mention"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <ReactTooltip id='search-keywords-tooltip' place="right" effect="solid" backgroundColor="white" textColor="black" />

            <div className="flex flex-wrap">
              {keywords.map(keyword => (
                <div key={keyword} className={`rounded-sm text-sm bg-gray-700 text-white flex items-center mb-2 items-stretch ${false ? 'p-0' : 'p-2 mr-2'}`}>
                  <div className="mr-2 flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 cursor-pointer opacity-50 hover:opacity-75" fill="none" viewBox="0 0 24 24" stroke="currentColor"
                      onClick={() => clearKeyword(keyword)}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <div className={`whitespace-nowrap flex-shrink-0 ${false ? '' : 'overflow-ellipsis truncate flex-1'}`}>
                    {keyword}
                  </div>
                </div>
              ))} 

              <div className="rounded-sm text-sm bg-gray-700 text-white flex mb-2 mr-2 items-center p-2">
                <div className="mr-2 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </div>
                <div className="whitespace-nowrap overflow-ellipsis flex-grow">
                  <input id="new-discover-keyword" type="text" className="bg-transparent text-sm border-none p-0 w-full focus:ring-0"
                    placeholder="New Keyword" autoComplete="off" spellCheck="off"
                    autoFocus={!keywords.length}
                    onKeyDown={(event) => {
                      if(event.key === 'Enter') {
                        const value = event.target.value;
                        if (value && value !== ''){
                          addKeyword(value);
                          document.getElementById('new-discover-keyword').value = '';
                        }      
                      }
                    }}
                  />
                </div>
              </div>
            </div> */}

            <div id="results-container" className={`h-96 overflow-auto pb-4`}>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 pr-2">
                {(results || []).map(c => (
                  <div key={c.name}
                    className={`rounded-md shadow-lg bg-gray-700 border-2 border-solid p-4 cursor-pointer text-white ${selectedSubs.includes(c.name) ? 'border-cyan-500' : 'border-transparent hover:border-gray-300'}`}
                    onClick={(e) => {
                      if (selectedSubs.includes(c.name)){
                        setSelectedSubs(selectedSubs.filter(s => s !== c.name));
                      } else {
                        setSelectedSubs([...selectedSubs, c.name]);
                      }
                    }}
                  >
                    <ErrorBoundary>
                      <div className="flex items-center">
                        {c.data && c.data.icon ? (
                          <img className="h-10 w-10 mr-2 rounded-md" src={c.data.icon} alt={c.name} />
                        ) : (
                          <div className="h-10 w-10 mr-2 rounded-md bg-gray-600">
                            <svg className="h-6 w-6 m-2 opacity-25" enableBackground="new 0 0 24 24" height="512" fill="currentColor" viewBox="0 0 24 24" width="512" xmlns="http://www.w3.org/2000/svg">
                              <path d="m21.325 9.308c-.758 0-1.425.319-1.916.816-1.805-1.268-4.239-2.084-6.936-2.171l1.401-6.406 4.461 1.016c0 1.108.89 2.013 1.982 2.013 1.113 0 2.008-.929 2.008-2.038s-.889-2.038-2.007-2.038c-.779 0-1.451.477-1.786 1.129l-4.927-1.108c-.248-.067-.491.113-.557.365l-1.538 7.062c-2.676.113-5.084.928-6.895 2.197-.491-.518-1.184-.837-1.942-.837-2.812 0-3.733 3.829-1.158 5.138-.091.405-.132.837-.132 1.268 0 4.301 4.775 7.786 10.638 7.786 5.888 0 10.663-3.485 10.663-7.786 0-.431-.045-.883-.156-1.289 2.523-1.314 1.594-5.115-1.203-5.117zm-15.724 5.41c0-1.129.89-2.038 2.008-2.038 1.092 0 1.983.903 1.983 2.038 0 1.109-.89 2.013-1.983 2.013-1.113.005-2.008-.904-2.008-2.013zm10.839 4.798c-1.841 1.868-7.036 1.868-8.878 0-.203-.18-.203-.498 0-.703.177-.18.491-.18.668 0 1.406 1.463 6.07 1.488 7.537 0 .177-.18.491-.18.668 0 .207.206.207.524.005.703zm-.041-2.781c-1.092 0-1.982-.903-1.982-2.011 0-1.129.89-2.038 1.982-2.038 1.113 0 2.008.903 2.008 2.038-.005 1.103-.895 2.011-2.008 2.011z"/>
                            </svg>
                          </div>
                        )}
              
                        <h2 className="text-md whitespace-nowrap overflow-ellipsis flex-shrink-1 truncate">
                          <span className="font-semibold">
                            r/{ c.name }
                          </span>
                          <div className="text-xs font-base opacity-50">
                            {c.data ? (
                              <span>{readableNumber(c.data.count_users)} members</span>
                            ) : (
                              <span>Fetching Details...</span>
                            )}
                          </div>
                        </h2>
              
                        {false ? (
                          <div className="flex items-center text-xs ml-auto text-green-400">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <div>Included in Audience</div>
                          </div>
                        ) : ''}
                      </div>
              

                      <div className="mt-2">
                        {c.data ? (
                          <div>
                            {c.data && c.data.tags ? (
                              <div>
                                <SubredditTags subreddit={c.data} tags={c.data.tags} />
                              </div>
                            ) : ''}
                            <p className={`text-xs overflow-ellipsis line-clamp-3 ${c.data?.description ? 'opacity-100' : 'opacity-50'}`}>
                              { c.data && c.data.description || 'No Description...' }
                            </p>
                          </div>
                        ) : <div className="h-12"></div>}
                      </div>
                    </ErrorBoundary>
                  </div>
                ))}
              </div>
            </div>
            

            <div className="border-t border-solid border-gray-700 w-full flex items-center pt-4">
              <div className="mr-auto">
                {!currentCollection ? (
                  <Link className="flex-shrink-0 inline-flex p-2 items-center justify-center focus:outline-none focus:ring-transparent focus:border-none rounded-lg text-gray-400 hover:bg-gray-700" to="/audiences/new/">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back
                  </Link>
                ) : ''}
                {/* <Popover className="relative">
                  {({ open }) => (
                    <>
                      <Popover.Button
                        className={`flex-shrink-0 inline-flex p-2 items-center justify-center focus:outline-none focus:ring-transparent focus:border-none rounded-lg ${open ? 'bg-gray-900 text-white' : 'text-gray-400 hover:bg-gray-700'}`}
                      >
                        <span className="sr-only">Search Tips</span>
                        <span>Tips</span>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </Popover.Button>

                      <Transition
                        show={open}
                        as={React.Fragment}
                        enter="transition ease-out duration-200"
                        enterFrom="opacity-0 translate-y-1"
                        enterTo="opacity-100 translate-y-0"
                        leave="transition ease-in duration-150"
                        leaveFrom="opacity-100 translate-y-0"
                        leaveTo="opacity-0 translate-y-1"
                      >
                        <Popover.Panel
                          static
                          className="absolute z-10 bg-white text-gray-900 shadow-lg rounded-lg left-20 bottom-0 p-2 w-screen max-w-xs sm:max-w-md"
                        >
                          <ul className="p-2 list-disc pl-8 text-sm">
                            <li>Pick 2-4 words your target audience might mention</li>
                            <li>Short & broad synonyms for your audience work well</li>
                            <li>Similar words like "Marketing" and "Marketers" are fine</li>
                            <li>The more keywords, the more refined the results get</li>
                            <li>For more info, read the full <a className="underline" href="https://gummysearch.com/how-to/discover-subreddits/" target="_blank">
                              guide on finding subreddits
                            </a></li>
                          </ul>
                        </Popover.Panel>
                      </Transition>
                    </>
                  )}
                </Popover> */}
              </div>

              {selectedSubs.length !== 0 ? (
                <div className="px-4 text-sm">
                  <div>{selectedSubs.length} Subreddit{selectedSubs.length === 1 ? '' : 's'} Selected</div>
                  <div className="text-xs opacity-50">
                    {readableNumber(results.filter(
                      c => selectedSubs.includes(c.name) && c.data && c.data.count_users
                    ).map(s => s.data.count_users).reduce((partial_sum, a) => partial_sum + a, 0))} Members
                  </div>
                </div>
              ) : (
                <div className="px-4 text-sm flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-yellow-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div>None Selected Yet</div>
                </div>
              )}

              <button
                type="submit"
                onClick={() => addToAudience()}
                disabled={selectedSubs.length === 0 || creating}
                className={`text-center px-4 py-2 border border-solid border-transparent text-sm font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 ${selectedSubs.length === 0 ? 'bg-gray-700 hover:bg-gray-700' : 'bg-cyan-600 hover:bg-cyan-700'}`}
              >
                {selectedSubs.length !== 0 ? (currentCollection ? 'Add to Audience' : 'Create Audience') : 'Select Relevant Subreddits To Continue'}
              </button>
            </div>
          </React.Fragment>
        )}

        {error ? (
          <ErrorBar error={error} setError={setError} onRetry={() => performSearch(keywords)}/>
        ) : ''}
      </ErrorBoundary>
    </div>
  );
}

const mapStateToProps = state => {
  return {
    
  }
};

const mapDispatchToProps = (dispatch) => {
  const fetchAudiences = (callback, onError) => {
    dispatch(audiencesOperations.fetchAudiences(callback, onError))
  };
  return {
    fetchAudiences,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(AudienceDiscoveryNew);

